(() => {
  const root = document.documentElement
  const page = document.querySelector('.tb-page')

  /* Mobile nav toggle — full-width bar over logo; X stays right */
  const header = document.querySelector('.tb-header')
  const toggle = document.querySelector('.tb-header__toggle')
  const nav = document.getElementById('tb-primary-nav')
  if (header && toggle && nav) {
    const mobileNav = window.matchMedia('(max-width: 899px)')
    const setOpen = (open) => {
      if (!mobileNav.matches) open = false
      header.classList.toggle('is-open', open)
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    }
    const onViewportChange = () => {
      if (!mobileNav.matches) setOpen(false)
    }
    window.addEventListener('resize', onViewportChange, { passive: true })
    mobileNav.addEventListener?.('change', onViewportChange)
    toggle.addEventListener('click', () => {
      if (!mobileNav.matches) return
      setOpen(!header.classList.contains('is-open'))
    })
    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setOpen(false))
    })
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false)
    })
    document.addEventListener('click', (event) => {
      if (!mobileNav.matches) return
      if (!header.contains(event.target)) setOpen(false)
    })
  }

  /* Photo mosaic — shared by hero + CTA banner */
  const PHOTO_POOL = [
    { src: '/assets/optimized/rev-athens.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-cannes-boat.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-founders.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-malta.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-mexico-city.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-milan.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-panama-city.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-paris.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-saigon.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-seoul.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-trieste.webp', kind: 'portrait' },
    { src: '/assets/optimized/rev-tapas-spain.webp', kind: 'square' },
    { src: '/assets/optimized/rev-toronto-shop.webp', kind: 'square' },
    { src: '/assets/optimized/rev-bali-sunset.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-bogota.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-bucharest.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-budapest.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-hallstatt.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-istanbul.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-papeete.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-shanghai-boat.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-shanghai-food.webp', kind: 'landscape' },
    { src: '/assets/optimized/rev-tulum.webp', kind: 'landscape' },
  ]

  /**
   * Format matcher — maps a cell's real pixel ratio to the closest photo format.
   * Always returns a match (never rejects layouts).
   *
   *   3:4  → portrait
   *   1:1  → square
   *   4:3  → landscape
   *   3:2  → landscape
   */
  const ASPECT_TARGETS = [
    { id: '3:4', ratio: 3 / 4, kind: 'portrait' },
    { id: '1:1', ratio: 1, kind: 'square' },
    { id: '4:3', ratio: 4 / 3, kind: 'landscape' },
    { id: '3:2', ratio: 3 / 2, kind: 'landscape' },
  ]

  /**
   * Actual cell pixel aspect ≈ (spanW/spanH) × (rows/cols) × containerAspect.
   * Grid units alone are wrong on a wide desktop hero.
   */
  const cellPixelAspect = (w, h, cols, rows, containerAspect) =>
    (w / Math.max(h, 0.001)) * (rows / cols) * Math.max(containerAspect, 0.2)

  const formatForAspect = (aspect) => {
    let best = ASPECT_TARGETS[0]
    let bestScore = Infinity
    for (const target of ASPECT_TARGETS) {
      const score = Math.abs(Math.log(Math.max(aspect, 0.05) / target.ratio))
      if (score < bestScore) {
        bestScore = score
        best = target
      }
    }
    return best
  }

  const kindFromAspect = (aspect) => formatForAspect(aspect).kind

  const kindForSpan = (w, h, cols = 6, rows = 5, containerAspect = 1) =>
    kindFromAspect(cellPixelAspect(w, h, cols, rows, containerAspect))

  /** Photo kinds allowed for a cell format — closest family only. */
  const kindsForCell = (cellKind) => {
    if (cellKind === 'portrait') return ['portrait']
    if (cellKind === 'landscape' || cellKind === 'wide') return ['landscape']
    return ['square', 'portrait']
  }

  const CTA_LAYOUTS = [
    [
      { c: 1, r: 1, w: 2, h: 4, kind: 'portrait' },
      { c: 3, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 5, r: 1, w: 2, h: 2, kind: 'portrait' },
      { c: 3, r: 3, w: 4, h: 2, kind: 'wide' },
    ],
    [
      { c: 1, r: 1, w: 6, h: 2, kind: 'wide' },
      { c: 1, r: 3, w: 2, h: 2, kind: 'portrait' },
      { c: 3, r: 3, w: 2, h: 2, kind: 'square' },
      { c: 5, r: 3, w: 2, h: 2, kind: 'portrait' },
    ],
    [
      { c: 1, r: 1, w: 3, h: 2, kind: 'landscape' },
      { c: 4, r: 1, w: 3, h: 2, kind: 'landscape' },
      { c: 1, r: 3, w: 2, h: 2, kind: 'portrait' },
      { c: 3, r: 3, w: 2, h: 2, kind: 'square' },
      { c: 5, r: 3, w: 2, h: 2, kind: 'portrait' },
    ],
    [
      { c: 1, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 3, r: 1, w: 2, h: 4, kind: 'portrait' },
      { c: 5, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 1, r: 3, w: 2, h: 2, kind: 'portrait' },
      { c: 5, r: 3, w: 2, h: 2, kind: 'landscape' },
    ],
    [
      { c: 1, r: 1, w: 4, h: 2, kind: 'wide' },
      { c: 5, r: 1, w: 2, h: 4, kind: 'portrait' },
      { c: 1, r: 3, w: 2, h: 2, kind: 'portrait' },
      { c: 3, r: 3, w: 2, h: 2, kind: 'square' },
    ],
    [
      { c: 1, r: 1, w: 2, h: 2, kind: 'portrait' },
      { c: 3, r: 1, w: 4, h: 2, kind: 'wide' },
      { c: 1, r: 3, w: 3, h: 2, kind: 'landscape' },
      { c: 4, r: 3, w: 3, h: 2, kind: 'landscape' },
    ],
    [
      { c: 1, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 3, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 5, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 1, r: 3, w: 6, h: 2, kind: 'wide' },
    ],
    [
      { c: 1, r: 1, w: 2, h: 4, kind: 'portrait' },
      { c: 3, r: 1, w: 2, h: 2, kind: 'landscape' },
      { c: 5, r: 1, w: 2, h: 2, kind: 'square' },
      { c: 3, r: 3, w: 2, h: 2, kind: 'square' },
      { c: 5, r: 3, w: 2, h: 2, kind: 'portrait' },
    ],
  ]

  /* Fallback only — irregular packer is preferred */
  const HERO_LAYOUTS = [
    [
      { c: 1, r: 1, w: 2, h: 4, kind: 'portrait' },
      { c: 3, r: 1, w: 4, h: 2, kind: 'landscape' },
      { c: 3, r: 3, w: 2, h: 3, kind: 'portrait' },
      { c: 5, r: 3, w: 2, h: 2, kind: 'landscape' },
      { c: 1, r: 5, w: 2, h: 1, kind: 'landscape' },
      { c: 5, r: 5, w: 2, h: 1, kind: 'wide' },
    ],
  ]

  const shuffle = (arr) => {
    const next = [...arr]
    for (let i = next.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[next[i], next[j]] = [next[j], next[i]]
    }
    return next
  }

  /** Build an irregular mosaic using real container aspect so portrait/landscape are true. */
  const packIrregularMosaic = (cols = 6, rows = 5, containerAspect = 1.4) => {
    const occupied = Array.from({ length: rows }, () => Array(cols).fill(false))
    const slots = []

    const shapes = [
      // Main tiles — at least 2 rows so we avoid ultra-thin ribbons
      { w: 2, h: 5 },
      { w: 2, h: 4 },
      { w: 2, h: 3 },
      { w: 3, h: 2 },
      { w: 4, h: 2 },
      { w: 5, h: 2 },
      { w: 6, h: 2 },
      { w: 3, h: 3 },
      { w: 4, h: 3 },
      { w: 2, h: 2 },
    ]

    const canPlace = (c0, r0, w, h) => {
      if (c0 < 0 || r0 < 0 || c0 + w > cols || r0 + h > rows) return false
      for (let r = r0; r < r0 + h; r += 1) {
        for (let c = c0; c < c0 + w; c += 1) {
          if (occupied[r][c]) return false
        }
      }
      return true
    }

    const mark = (c0, r0, w, h) => {
      for (let r = r0; r < r0 + h; r += 1) {
        for (let c = c0; c < c0 + w; c += 1) occupied[r][c] = true
      }
    }

    const freeCells = () => {
      const cells = []
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          if (!occupied[r][c]) cells.push({ c, r })
        }
      }
      return cells
    }

    const pushSlot = (c0, r0, w, h) => {
      mark(c0, r0, w, h)
      const aspect = cellPixelAspect(w, h, cols, rows, containerAspect)
      const format = formatForAspect(aspect)
      slots.push({
        c: c0 + 1,
        r: r0 + 1,
        w,
        h,
        kind: format.kind,
        formatId: format.id,
      })
    }

    const place = (shape) => {
      const cells = freeCells()
      if (!cells.length) return false
      for (let i = cells.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[cells[i], cells[j]] = [cells[j], cells[i]]
      }
      for (const origin of cells) {
        if (!canPlace(origin.c, origin.r, shape.w, shape.h)) continue
        pushSlot(origin.c, origin.r, shape.w, shape.h)
        return true
      }
      return false
    }

    // Prefer shapes that are truly portrait / landscape in *pixels* for this viewport
    const shaped = shapes.map((s) => ({
      ...s,
      kind: kindForSpan(s.w, s.h, cols, rows, containerAspect),
    }))
    const portraits = shaped.filter((s) => s.kind === 'portrait')
    const landscapes = shaped.filter((s) => s.kind === 'landscape' || s.kind === 'wide')
    const squares = shaped.filter((s) => s.kind === 'square')

    // Seed one real portrait + one real landscape when possible
    if (portraits.length) place(portraits[Math.floor(Math.random() * portraits.length)])
    if (landscapes.length) place(landscapes[Math.floor(Math.random() * landscapes.length)])

    for (let pass = 0; pass < 50; pass += 1) {
      if (!freeCells().length) break
      // Bias toward clear portrait/landscape over ambiguous squares
      const poolShapes = [
        ...shuffle(portraits),
        ...shuffle(landscapes),
        ...shuffle(squares),
        ...shuffle(shaped),
      ]
      let placed = false
      for (const shape of poolShapes) {
        if (place(shape)) {
          placed = true
          break
        }
      }
      if (!placed) break
    }

    for (let guard = 0; guard < cols * rows; guard += 1) {
      const cells = freeCells()
      if (!cells.length) break
      const { c, r } = cells[0]
      // Prefer chunky fillers; thin 1-row strips only as last resort
      const fillers = [
        { w: 2, h: 2 },
        { w: 3, h: 2 },
        { w: 2, h: 3 },
        { w: 3, h: 3 },
        { w: 2, h: 1 },
        { w: 3, h: 1 },
        { w: 1, h: 2 },
        { w: 1, h: 1 },
      ]
      let done = false
      for (const shape of fillers) {
        if (canPlace(c, r, shape.w, shape.h)) {
          pushSlot(c, r, shape.w, shape.h)
          done = true
          break
        }
      }
      if (!done) pushSlot(c, r, 1, 1)
    }

    return slots
  }

  const initPhotoMosaic = ({
    root,
    gridSel,
    cellClass,
    shotClass,
    layouts,
    cdnAttr,
    interval = 2800,
    startDelay = 900,
    irregular = false,
    cols = 6,
    rows = 4,
  }) => {
    if (!root) return
    const grid = root.querySelector(gridSel)
    if (!grid) return
    const cdn = root.getAttribute(cdnAttr) || 'https://wear2where-preview.vercel.app'
    const customImgs = [...(root.querySelector('[data-cta-photo-pool]')?.content?.querySelectorAll('img') || [])]
    const customPool = customImgs
      .map((img) => ({ src: img.getAttribute('src') || '', kind: img.getAttribute('data-kind') || 'portrait' }))
      .filter((p) => p.src)
    const pool = customPool.length ? customPool : PHOTO_POOL

    const toUrl = (src) => (src.startsWith('http') ? src : `${cdn}${src}`)

    const readContainerAspect = () => {
      const rect = grid.getBoundingClientRect()
      if (rect.width > 8 && rect.height > 8) return rect.width / rect.height
      // Hero often measures 0 height before paint — estimate from parent clip
      const parent = grid.parentElement
      const pr = parent?.getBoundingClientRect?.()
      if (pr && pr.width > 8 && pr.height > 8) return pr.width / pr.height
      // Desktop-ish fallback (wide), mobile closer to square
      return window.matchMedia('(max-width: 639px)').matches ? 0.85 : 1.75
    }

    const makeBags = () => ({
      portrait: shuffle(pool.filter((p) => p.kind === 'portrait')),
      square: shuffle(pool.filter((p) => p.kind === 'square')),
      landscape: shuffle(pool.filter((p) => p.kind === 'landscape')),
      wide: shuffle(pool.filter((p) => p.kind === 'wide')),
    })

    const pickPhoto = (bags, cellKind) => {
      const order = kindsForCell(cellKind)
      for (const key of order) {
        if (!bags[key]?.length) {
          bags[key] = shuffle(pool.filter((p) => p.kind === key))
        }
        if (bags[key]?.length) return bags[key].pop()
      }
      const fallbackKey = cellKind === 'portrait' || cellKind === 'square' ? 'portrait' : 'landscape'
      return shuffle(pool.filter((p) => p.kind === fallbackKey))[0] || pool[0]
    }

    const usedSrcs = () =>
      new Set(
        [...grid.querySelectorAll(`.${cellClass}`)]
          .map((cell) => cell.getAttribute('data-src'))
          .filter(Boolean),
      )

    const nextPhotoFor = (cellKind, currentSrc) => {
      const used = usedSrcs()
      const order = kindsForCell(cellKind)
      for (const key of order) {
        const candidates = shuffle(
          pool.filter((p) => p.kind === key && p.src !== currentSrc && !used.has(p.src)),
        )
        if (candidates.length) return candidates[0]
      }
      for (const key of order) {
        const candidates = shuffle(pool.filter((p) => p.kind === key && p.src !== currentSrc))
        if (candidates.length) return candidates[0]
      }
      return null
    }

    const makeShot = (src, active) => {
      const img = document.createElement('img')
      img.className = `${shotClass}${active ? ' is-active' : ''}`
      img.src = toUrl(src)
      img.alt = ''
      img.width = 900
      img.height = 900
      img.decoding = 'async'
      return img
    }

    const swapCell = (cell) => {
      if (!cell || cell.classList.contains('is-swapping')) return
      // Re-check real pixel aspect in case layout shifted
      const rect = cell.getBoundingClientRect()
      let kind = cell.getAttribute('data-kind') || 'square'
      if (rect.width > 4 && rect.height > 4) {
        const format = formatForAspect(rect.width / rect.height)
        kind = format.kind
        cell.setAttribute('data-kind', format.kind)
        cell.setAttribute('data-format', format.id)
        cell.className = `${cellClass} ${cellClass}--${format.kind}`
      }
      const current = cell.getAttribute('data-src') || ''
      const photo = nextPhotoFor(kind, current)
      if (!photo || photo.src === current) return

      const active = cell.querySelector(`.${shotClass}.is-active`)
      const idle = cell.querySelector(`.${shotClass}:not(.is-active)`)
      if (!active || !idle) return

      cell.classList.add('is-swapping')
      let done = false
      const apply = () => {
        if (done) return
        done = true
        idle.onload = null
        idle.onerror = null
        idle.classList.add('is-active')
        active.classList.remove('is-active')
        cell.setAttribute('data-src', photo.src)
        window.setTimeout(() => {
          cell.classList.remove('is-swapping')
        }, 480)
      }

      idle.onload = () => apply()
      idle.onerror = () => {
        done = true
        cell.classList.remove('is-swapping')
      }
      idle.src = toUrl(photo.src)
      if (idle.complete && idle.naturalWidth) apply()
    }

    /** After paint: remap each cell to the closest format + matching photo. */
    const reconcileMeasuredKinds = () => {
      const bags = makeBags()
      ;[...grid.querySelectorAll(`.${cellClass}`)].forEach((cell) => {
        const rect = cell.getBoundingClientRect()
        if (rect.width < 4 || rect.height < 4) return
        const format = formatForAspect(rect.width / rect.height)
        const kind = format.kind
        const src = cell.getAttribute('data-src') || ''
        const photo = pool.find((p) => p.src === src)
        const ok = photo && kindsForCell(kind).includes(photo.kind)

        cell.setAttribute('data-kind', kind)
        cell.setAttribute('data-format', format.id)
        cell.className = `${cellClass} ${cellClass}--${kind}`
        if (ok) return

        const next = pickPhoto(bags, kind)
        if (!next) return
        cell.setAttribute('data-src', next.src)
        const shots = [...cell.querySelectorAll(`.${shotClass}`)]
        shots.forEach((img, i) => {
          img.src = toUrl(next.src)
          img.classList.toggle('is-active', i === 0)
        })
      })
    }

    const render = () => {
      const containerAspect = readContainerAspect()
      const bags = makeBags()
      let layout = irregular ? packIrregularMosaic(cols, rows, containerAspect) : null
      if (!layout?.length) {
        const base = layouts[Math.floor(Math.random() * layouts.length)]
        layout = base.map((slot) => {
          const aspect = cellPixelAspect(slot.w, slot.h, cols, rows, containerAspect)
          const format = formatForAspect(aspect)
          return {
            ...slot,
            kind: format.kind,
            formatId: format.id,
          }
        })
      }
      const frag = document.createDocumentFragment()

      layout.forEach((slot, index) => {
        const aspect = cellPixelAspect(slot.w, slot.h, cols, rows, containerAspect)
        const format = slot.formatId
          ? ASPECT_TARGETS.find((t) => t.id === slot.formatId) || formatForAspect(aspect)
          : formatForAspect(aspect)
        const kind = slot.kind || format.kind
        const photo = pickPhoto(bags, kind)
        if (!photo) return
        const figure = document.createElement('figure')
        figure.className = `${cellClass} ${cellClass}--${kind}`
        figure.setAttribute('data-kind', kind)
        figure.setAttribute('data-format', format.id)
        figure.setAttribute('data-src', photo.src)
        figure.style.gridColumn = `${slot.c} / span ${slot.w}`
        figure.style.gridRow = `${slot.r} / span ${slot.h}`
        const shotA = makeShot(photo.src, true)
        const shotB = makeShot(photo.src, false)
        shotA.loading = index < 2 ? 'eager' : 'lazy'
        shotB.loading = 'lazy'
        if (index === 0) shotA.fetchPriority = 'high'
        figure.append(shotA, shotB)
        frag.appendChild(figure)
      })

      grid.replaceChildren(frag)
      requestAnimationFrame(() => {
        reconcileMeasuredKinds()
        requestAnimationFrame(reconcileMeasuredKinds)
      })
    }

    render()

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let timer = 0
    let lastAspect = readContainerAspect()
    let resizeTimer = 0

    const tick = () => {
      const cells = [...grid.querySelectorAll(`.${cellClass}`)]
      if (!cells.length) return
      const open = shuffle(cells.filter((cell) => !cell.classList.contains('is-swapping')))
      if (!open.length) return
      const count = Math.min(open.length, Math.random() > 0.45 ? 2 : 1)
      open.slice(0, count).forEach((cell) => swapCell(cell))
    }

    const start = () => {
      if (reduce || timer) return
      timer = window.setInterval(tick, interval)
    }
    const stop = () => {
      if (!timer) return
      window.clearInterval(timer)
      timer = 0
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop()
      else start()
    })

    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer)
        resizeTimer = window.setTimeout(() => {
          const next = readContainerAspect()
          if (Math.abs(next - lastAspect) / Math.max(lastAspect, 0.2) > 0.12) {
            lastAspect = next
            render()
          } else {
            reconcileMeasuredKinds()
          }
        }, 180)
      },
      { passive: true },
    )

    window.setTimeout(start, startDelay)
  }

  initPhotoMosaic({
    root: document.querySelector('[data-hero-mosaic]'),
    gridSel: '[data-hero-grid]',
    cellClass: 'tb-hero__cell',
    shotClass: 'tb-hero__shot',
    layouts: HERO_LAYOUTS,
    cdnAttr: 'data-hero-cdn',
    interval: 3100,
    startDelay: 700,
    irregular: true,
    cols: 6,
    rows: 5,
  })

  initPhotoMosaic({
    root: document.querySelector('[data-cta-mosaic]'),
    gridSel: '[data-cta-grid]',
    cellClass: 'tb-cta__cell',
    shotClass: 'tb-cta__shot',
    layouts: CTA_LAYOUTS,
    cdnAttr: 'data-cta-cdn',
    interval: 3100,
    startDelay: 1000,
    irregular: true,
    cols: 6,
    rows: 4,
  })

  /* Hero photo carousel — 6s, hover pause, swipe, arrows, dots */
  const heroCarousel = document.querySelector('[data-hero-carousel]')
  if (heroCarousel) {
    const root = heroCarousel
    const slidesRoot = root.querySelector('[data-hero-slides]')
    const slides = slidesRoot ? [...slidesRoot.querySelectorAll('.tb-hero__slide')] : []
    const tag = root.querySelector('[data-hero-tag]')
    const dots = [...root.querySelectorAll('[data-hero-dot]')]
    const prevBtn = root.querySelector('[data-hero-prev]')
    const nextBtn = root.querySelector('[data-hero-next]')
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const intervalMs = 6000
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')))
    if (index < 0) index = 0
    let timer = 0
    let touchStartX = 0
    let touchStartY = 0
    let touching = false

    const applyTagSide = (slide) => {
      if (!tag) return
      const side = slide?.getAttribute('data-tag-side') === 'left' ? 'left' : 'right'
      tag.classList.toggle('tb-hero__tag--left', side === 'left')
      tag.classList.toggle('tb-hero__tag--right', side === 'right')
    }

    const syncDots = () => {
      dots.forEach((dot, i) => {
        const on = i === index
        dot.classList.toggle('is-active', on)
        dot.setAttribute('aria-selected', on ? 'true' : 'false')
      })
    }

    const applyMobileFrame = (slide) => {
      const hasMobile = slide?.getAttribute('data-has-mobile') === 'true'
      root.classList.toggle('has-mobile-shot', hasMobile)
    }

    const show = (next) => {
      if (slides.length < 1) return
      slides[index]?.classList.remove('is-active')
      index = ((next % slides.length) + slides.length) % slides.length
      const active = slides[index]
      active.classList.add('is-active')
      const isIntro = active.getAttribute('data-intro') === 'true'
      root.classList.toggle('is-intro', isIntro)
      applyTagSide(active)
      applyMobileFrame(active)
      syncDots()
    }

    const stop = () => {
      if (timer) {
        window.clearInterval(timer)
        timer = 0
      }
    }

    const start = () => {
      if (reduce || slides.length < 2 || timer) return
      timer = window.setInterval(() => show(index + 1), intervalMs)
    }

    const restart = () => {
      stop()
      start()
    }

    applyTagSide(slides[index])
    applyMobileFrame(slides[index])
    syncDots()
    root.classList.toggle('is-intro', slides[index]?.getAttribute('data-intro') === 'true')

    if (slides.length > 1) {
      start()
      root.addEventListener('mouseenter', stop)
      root.addEventListener('mouseleave', start)
      root.addEventListener('focusin', stop)
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) start()
      })
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop()
        else start()
      })

      prevBtn?.addEventListener('click', () => {
        show(index - 1)
        restart()
      })
      nextBtn?.addEventListener('click', () => {
        show(index + 1)
        restart()
      })
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = Number(dot.getAttribute('data-hero-dot'))
          if (!Number.isNaN(i)) {
            show(i)
            restart()
          }
        })
      })

      const stage = root.querySelector('.tb-hero__media-clip') || root
      stage.addEventListener(
        'touchstart',
        (event) => {
          const t = event.changedTouches?.[0]
          if (!t) return
          touching = true
          touchStartX = t.clientX
          touchStartY = t.clientY
          stop()
        },
        { passive: true }
      )
      stage.addEventListener(
        'touchend',
        (event) => {
          if (!touching) return
          touching = false
          const t = event.changedTouches?.[0]
          if (!t) {
            start()
            return
          }
          const dx = t.clientX - touchStartX
          const dy = t.clientY - touchStartY
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.15) {
            show(dx < 0 ? index + 1 : index - 1)
          }
          restart()
        },
        { passive: true }
      )
    }

    /* Tag rides page scroll down until just above the place pill (desktop only) */
    if (tag && !reduce) {
      const clip = root.querySelector('.tb-hero__media-clip') || root
      const glass = root.querySelector('.tb-hero__glass')
      const mobileMq = window.matchMedia('(max-width: 899px)')
      let tagScrollRaf = 0
      let maxTravel = 0

      const measureTagTravel = () => {
        tag.style.setProperty('--tb-hero-tag-y', '0px')
        if (mobileMq.matches) {
          maxTravel = 0
          return
        }
        const place =
          root.querySelector('.tb-hero__slide.is-active .tb-hero__place') ||
          root.querySelector('.tb-hero__place')
        if (!place) {
          maxTravel = 0
          return
        }
        const tagRect = tag.getBoundingClientRect()
        const placeRect = place.getBoundingClientRect()
        let stopTop = placeRect.top - 14
        if (glass) {
          const glassTop = glass.getBoundingClientRect().top
          stopTop = Math.min(stopTop, glassTop - 18)
        }
        maxTravel = Math.max(0, stopTop - tagRect.bottom)
      }

      const updateTagScroll = () => {
        tagScrollRaf = 0
        if (mobileMq.matches || maxTravel <= 0) {
          tag.style.setProperty('--tb-hero-tag-y', '0px')
          return
        }
        const clipRect = clip.getBoundingClientRect()
        const scrollSpan = Math.max(120, clipRect.height * 0.55)
        const scrolled = Math.min(Math.max(0, -clipRect.top), scrollSpan)
        const progress = scrolled / scrollSpan
        tag.style.setProperty('--tb-hero-tag-y', `${(maxTravel * progress).toFixed(1)}px`)
      }

      const requestTagScroll = () => {
        if (tagScrollRaf) return
        tagScrollRaf = window.requestAnimationFrame(updateTagScroll)
      }

      const remountTravel = () => {
        measureTagTravel()
        requestTagScroll()
      }

      window.addEventListener('scroll', requestTagScroll, { passive: true })
      window.addEventListener('resize', remountTravel)
      if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', remountTravel)
      } else if (typeof mobileMq.addListener === 'function') {
        mobileMq.addListener(remountTravel)
      }
      const slideObserver = new MutationObserver(remountTravel)
      slides.forEach((slide) => {
        slideObserver.observe(slide, { attributes: true, attributeFilter: ['class'] })
      })
      // Wait a frame so layout/fonts settle
      window.requestAnimationFrame(remountTravel)
    }
  }

  /* Photo carousel (legacy section below hero, if present) */
  document.querySelectorAll('[data-photo-carousel]').forEach((root) => {
    const slides = [...root.querySelectorAll('.tb-carousel__slide')]
    if (slides.length < 2) return
    let index = 0
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const interval = 5200
    const show = (next) => {
      slides[index].classList.remove('is-active')
      index = (next + slides.length) % slides.length
      slides[index].classList.add('is-active')
    }
    if (!reduce) {
      window.setInterval(() => show(index + 1), interval)
    }
  })

  /* Stories fan — continuous offset, no wrap teleport */
  const storyFan = document.querySelector('[data-story-fan]')
  if (storyFan) {
    const stage = storyFan.querySelector('.tb-fan__stage')
    const cards = [...storyFan.querySelectorAll('[data-story-card]')]
    if (stage && cards.length > 1) {
      const n = cards.length
      const V = Math.min(7, n)
      const center = Math.floor((V - 1) / 2)
      const mobileMq = window.matchMedia('(max-width: 639px)')
      const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)')

      const SLOTS_DESKTOP = [
        { x: -3, rot: -22, y: 1.45, scale: 0.86, z: 1, bright: 0.92 },
        { x: -2, rot: -14, y: 0.8, scale: 0.9, z: 2, bright: 1 },
        { x: -1, rot: -7, y: 0.3, scale: 0.94, z: 4, bright: 1 },
        { x: 0, rot: 0, y: 0, scale: 1.14, z: 8, bright: 1 },
        { x: 1, rot: 7, y: 0.3, scale: 0.94, z: 4, bright: 1 },
        { x: 2, rot: 14, y: 0.8, scale: 0.9, z: 2, bright: 1 },
        { x: 3, rot: 22, y: 1.45, scale: 0.86, z: 1, bright: 0.92 },
      ]
      const SLOTS_MOBILE = [
        { x: -3, rot: -18, y: 1.05, scale: 0.86, z: 1, bright: 0.92 },
        { x: -2, rot: -12, y: 0.6, scale: 0.9, z: 2, bright: 1 },
        { x: -1, rot: -6, y: 0.2, scale: 0.94, z: 4, bright: 1 },
        { x: 0, rot: 0, y: 0, scale: 1.16, z: 8, bright: 1 },
        { x: 1, rot: 6, y: 0.2, scale: 0.94, z: 4, bright: 1 },
        { x: 2, rot: 12, y: 0.6, scale: 0.9, z: 2, bright: 1 },
        { x: 3, rot: 18, y: 1.05, scale: 0.86, z: 1, bright: 0.92 },
      ]

      let cachedStep = 120
      const measureStep = () => {
        const raw = getComputedStyle(storyFan).getPropertyValue('--tb-story-step').trim() || '7.5rem'
        const probe = document.createElement('div')
        probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;width:${raw}`
        document.body.appendChild(probe)
        cachedStep = probe.getBoundingClientRect().width || 120
        probe.remove()
        return cachedStep
      }
      measureStep()

      let offset = 0
      let floatOffset = 0
      let startX = 0
      let lastX = 0
      let lastT = 0
      let velocity = 0
      let dragging = false
      let moved = false
      let animating = false
      let activePointer = null
      let dragBase = 0
      let raf = 0
      let tapCard = null
      const TAP_PX = 12

      const mix = (a, b, t) => ({
        x: a.x + (b.x - a.x) * t,
        rot: a.rot + (b.rot - a.rot) * t,
        y: a.y + (b.y - a.y) * t,
        scale: a.scale + (b.scale - a.scale) * t,
        z: Math.round(a.z + (b.z - a.z) * t),
        bright: a.bright + (b.bright - a.bright) * t,
      })

      const slotsForViewport = () => (mobileMq.matches ? SLOTS_MOBILE : SLOTS_DESKTOP)

      /* Map fan index so center slot always uses the hero pose, even when n < 7 */
      const sampleSlot = (slotF) => {
        const slots = slotsForViewport()
        const mapped = slotF - center + (slots.length - 1) / 2
        if (mapped <= 0) {
          const s = slots[0]
          const t = Math.max(-0.55, slotF)
          return {
            ...s,
            x: s.x + (t < 0 ? t * 0.95 : 0),
            y: s.y - (t < 0 ? t * 0.2 : 0),
            scale: s.scale * (t < 0 ? 1 + t * 0.06 : 1),
            z: t < 0 ? 0 : s.z,
          }
        }
        if (mapped >= slots.length - 1) {
          const s = slots[slots.length - 1]
          const over = slotF - (V - 1)
          return {
            ...s,
            x: s.x + (over > 0 ? over * 0.95 : 0),
            y: s.y + (over > 0 ? over * 0.2 : 0),
            scale: s.scale * (over > 0 ? 1 - over * 0.06 : 1),
            z: over > 0 ? 0 : s.z,
          }
        }
        const i0 = Math.floor(mapped)
        const i1 = Math.min(slots.length - 1, i0 + 1)
        return mix(slots[i0], slots[i1], mapped - i0)
      }

      /* Stable display slot in (-0.5, n-0.5] — avoids modulo teleport mid-swipe */
      const displaySlot = (index, fo) => {
        let slot = index - fo
        slot = ((((slot + 0.5) % n) + n) % n) - 0.5
        return slot
      }

      const setLive = (on) => stage.classList.toggle('is-live', on)

      const paintFloat = (fo) => {
        floatOffset = fo
        cards.forEach((card, index) => {
          const slot = displaySlot(index, fo)
          if (slot < -0.5 || slot > V - 0.5) {
            card.dataset.slot = 'park'
            card.style.transform =
              'translateX(calc(-50% + 3.5 * var(--tb-story-step))) translateY(1.8rem) rotate(26deg) scale(0.8)'
            card.style.zIndex = '0'
            card.style.opacity = '0'
            card.style.filter = 'none'
            card.style.pointerEvents = 'none'
            card.removeAttribute('aria-current')
            return
          }

          /* Soft fade while a card wraps around the fan */
          let opacity = 1
          if (slot < 0) opacity = Math.max(0, 1 + slot / 0.5)
          else if (slot > V - 1) opacity = Math.max(0, 1 - (slot - (V - 1)) / 0.5)

          const s = sampleSlot(slot)
          const nearest = Math.max(0, Math.min(V - 1, Math.round(slot)))
          card.dataset.slot = String(nearest)
          card.style.transform =
            `translateX(calc(-50% + ${s.x} * var(--tb-story-step))) translateY(${s.y}rem) rotate(${s.rot}deg) scale(${s.scale})`
          card.style.zIndex = String(s.z)
          card.style.opacity = String(opacity)
          card.style.filter = s.bright < 0.999 ? `brightness(${s.bright})` : 'none'
          card.style.pointerEvents = opacity > 0.55 && Math.abs(slot - center) < 1.2 ? 'auto' : 'auto'
          if (Math.abs(slot - center) < 0.4) card.setAttribute('aria-current', 'true')
          else card.removeAttribute('aria-current')
        })
      }

      const settleTo = (target, duration = 400) => {
        cancelAnimationFrame(raf)
        if (reduceMq.matches) {
          offset = ((Math.round(target) % n) + n) % n
          floatOffset = offset
          paintFloat(offset)
          setLive(false)
          animating = false
          return
        }
        animating = true
        setLive(true)
        const from = floatOffset
        const dist = target - from
        if (Math.abs(dist) < 0.001) {
          offset = ((Math.round(target) % n) + n) % n
          floatOffset = offset
          paintFloat(offset)
          setLive(false)
          animating = false
          return
        }
        const ms = Math.max(240, Math.min(460, duration * (0.7 + Math.min(1, Math.abs(dist)) * 0.3)))
        const t0 = performance.now()
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / ms)
          const e = 1 - Math.pow(1 - t, 3)
          paintFloat(from + dist * e)
          if (t < 1) {
            raf = requestAnimationFrame(tick)
          } else {
            offset = ((Math.round(target) % n) + n) % n
            floatOffset = offset
            paintFloat(offset)
            setLive(false)
            animating = false
          }
        }
        raf = requestAnimationFrame(tick)
      }

      const onDown = (clientX, pointerId, cardEl) => {
        if (animating) {
          cancelAnimationFrame(raf)
          animating = false
        }
        dragging = true
        moved = false
        tapCard = cardEl || null
        velocity = 0
        startX = clientX
        lastX = clientX
        lastT = performance.now()
        activePointer = pointerId
        setLive(true)
        dragBase = floatOffset
        paintFloat(dragBase)
      }

      const onMove = (clientX) => {
        if (!dragging) return
        const now = performance.now()
        const dt = Math.max(1, now - lastT)
        velocity = ((clientX - lastX) / dt) * 16
        lastX = clientX
        lastT = now
        const dx = clientX - startX
        if (Math.abs(dx) > TAP_PX) moved = true
        if (!moved) return
        const delta = Math.max(-1.35, Math.min(1.35, -dx / cachedStep))
        paintFloat(dragBase + delta)
      }

      const onUp = () => {
        if (!dragging) return
        dragging = false
        activePointer = null

        /* Tap → open modal (do not start a settle that blocks the click) */
        if (!moved) {
          const card = tapCard
          tapCard = null
          floatOffset = dragBase
          paintFloat(dragBase)
          setLive(false)
          if (card) {
            const index = Number(card.dataset.index)
            if (!Number.isNaN(index)) {
              const slot = displaySlot(index, offset)
              if (Math.abs(slot - center) > 0.55) syncFanToIndex(index)
              openStoryModal(index)
            }
          }
          return
        }

        tapCard = null
        const pos = floatOffset
        let target = Math.round(pos)
        const flick = Math.abs(velocity) > 0.4
        if (flick) {
          target = velocity < 0 ? Math.floor(pos) + 1 : Math.ceil(pos) - 1
        } else if (Math.abs(pos - Math.round(dragBase)) < 0.14) {
          target = Math.round(dragBase)
        }
        settleTo(target, 360)
      }

      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        const card = event.target.closest?.('[data-story-card]')
        onDown(event.clientX, event.pointerId, card)
        try {
          stage.setPointerCapture(event.pointerId)
        } catch {
          /* ignore */
        }
      })
      stage.addEventListener(
        'pointermove',
        (event) => {
          if (activePointer != null && event.pointerId !== activePointer) return
          if (!dragging) return
          onMove(event.clientX)
          if (moved && event.pointerType === 'touch') event.preventDefault()
        },
        { passive: false }
      )
      const endPointer = (event) => {
        if (activePointer != null && event.pointerId !== activePointer) return
        onUp()
      }
      stage.addEventListener('pointerup', endPointer)
      stage.addEventListener('pointercancel', endPointer)
      stage.addEventListener(
        'touchmove',
        (event) => {
          if (dragging && moved) event.preventDefault()
        },
        { passive: false }
      )

      /* Story photo modal */
      const modal =
        storyFan.parentElement?.querySelector('[data-story-modal]') ||
        document.querySelector('[data-story-modal]')
      let modalIndex = 0

      const syncFanToIndex = (index) => {
        const goalBase = index - center
        let best = goalBase
        let bestDist = Infinity
        for (let k = -3; k <= 3; k += 1) {
          const cand = goalBase + k * n
          const d = Math.abs(cand - floatOffset)
          if (d < bestDist) {
            bestDist = d
            best = cand
          }
        }
        settleTo(best, 360)
      }

      const fillModal = (index) => {
        if (!modal) return
        modalIndex = ((index % n) + n) % n
        const card = cards[modalIndex]
        if (!card) return
        const img = modal.querySelector('[data-story-modal-img]')
        const label = modal.querySelector('[data-story-modal-label]')
        const caption = modal.querySelector('[data-story-modal-caption]')
        const description = modal.querySelector('[data-story-modal-description]')
        const link = modal.querySelector('[data-story-modal-link]')
        const src = card.getAttribute('data-src') || card.querySelector('img')?.src || ''
        const name = card.getAttribute('data-label') || ''
        const quote = card.getAttribute('data-caption') || ''
        const body = card.getAttribute('data-description') || ''
        const href = card.getAttribute('data-href')
        if (img) {
          img.src = src
          img.alt = name
        }
        if (label) label.textContent = name
        if (caption) {
          caption.textContent = quote
          caption.hidden = !quote
        }
        if (description) {
          description.textContent = body
          description.hidden = !body
        }
        if (link) {
          if (href) {
            link.href = href
            link.hidden = false
          } else {
            link.removeAttribute('href')
            link.hidden = true
          }
        }
      }

      const openStoryModal = (index) => {
        if (!modal) return
        fillModal(index)
        if (typeof modal.showModal === 'function') modal.showModal()
        else modal.setAttribute('open', '')
        document.documentElement.classList.add('tb-modal-open')
        modal.querySelector('[data-story-close]')?.focus()
      }

      const closeStoryModal = () => {
        if (!modal) return
        if (typeof modal.close === 'function') modal.close()
        else modal.removeAttribute('open')
        document.documentElement.classList.remove('tb-modal-open')
      }

      if (modal) {
        modal.querySelector('[data-story-close]')?.addEventListener('click', closeStoryModal)
        modal.querySelector('[data-story-prev]')?.addEventListener('click', () => {
          fillModal(modalIndex - 1)
          syncFanToIndex(modalIndex)
        })
        modal.querySelector('[data-story-next]')?.addEventListener('click', () => {
          fillModal(modalIndex + 1)
          syncFanToIndex(modalIndex)
        })
        modal.addEventListener('click', (event) => {
          if (event.target === modal) closeStoryModal()
        })
        modal.addEventListener('close', () => {
          document.documentElement.classList.remove('tb-modal-open')
        })
        document.addEventListener('keydown', (event) => {
          if (!modal.open) return
          if (event.key === 'ArrowLeft') {
            event.preventDefault()
            modal.querySelector('[data-story-prev]')?.click()
          } else if (event.key === 'ArrowRight') {
            event.preventDefault()
            modal.querySelector('[data-story-next]')?.click()
          }
        })
      }

      cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          /* Modal opens on pointer tap; suppress button default after drag */
          event.preventDefault()
        })
      })

      stage.addEventListener('keydown', (event) => {
        if (modal?.open || animating || dragging) return
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          settleTo(Math.round(floatOffset) - 1, 360)
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          settleTo(Math.round(floatOffset) + 1, 360)
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          const front = cards.find((card) => Math.abs(displaySlot(Number(card.dataset.index), offset) - center) < 0.5)
          if (front) openStoryModal(Number(front.dataset.index))
        }
      })

      const onViewportChange = () => {
        measureStep()
        paintFloat(floatOffset)
      }
      window.addEventListener('resize', onViewportChange, { passive: true })
      mobileMq.addEventListener?.('change', onViewportChange)

      storyFan.classList.add('is-ready')
      paintFloat(0)
    }
  }

  /* How it works — sample guides */
  const howSamples = document.querySelector('[data-how-samples]')
  if (howSamples) {
    const modal = howSamples.parentElement?.querySelector('[data-sample-modal]') || document.querySelector('[data-sample-modal]')
    const cards = [...howSamples.querySelectorAll('[data-sample-card]')]

    const openSample = (card) => {
      if (!modal || !card) return
      const img = modal.querySelector('[data-sample-modal-img]')
      const place = modal.querySelector('[data-sample-modal-place]')
      const title = modal.querySelector('[data-sample-modal-title]')
      const guide = modal.querySelector('[data-sample-modal-guide]')
      const city = card.getAttribute('data-city') || ''
      const country = card.getAttribute('data-country') || ''
      const src = card.getAttribute('data-image') || ''
      if (img) {
        img.src = src
        img.alt = city
      }
      if (place) place.textContent = [city, country].filter(Boolean).join(' · ')
      if (title) title.textContent = card.getAttribute('data-title') || city
      if (guide) {
        guide.innerHTML = ''
        const bodyId = card.getAttribute('data-body-id')
        const tpl = bodyId ? document.getElementById(bodyId) : null
        if (tpl?.content) {
          guide.appendChild(tpl.content.cloneNode(true))
        }
      }
      if (typeof modal.showModal === 'function') modal.showModal()
      else modal.setAttribute('open', '')
      document.documentElement.classList.add('tb-modal-open')
      modal.querySelector('[data-sample-close]')?.focus()
      modal.querySelector('.tb-sample-modal__body')?.scrollTo?.(0, 0)
    }

    const closeSample = () => {
      if (!modal) return
      if (typeof modal.close === 'function') modal.close()
      else modal.removeAttribute('open')
      document.documentElement.classList.remove('tb-modal-open')
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => openSample(card))
    })
    modal?.querySelector('[data-sample-close]')?.addEventListener('click', closeSample)
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) closeSample()
    })
    modal?.addEventListener('close', () => {
      document.documentElement.classList.remove('tb-modal-open')
    })
  }

  /* Horizontal galleries: moments */
  const bindHScroll = (viewportSel, barSel) => {
    const viewport = document.querySelector(viewportSel)
    const bar = document.querySelector(barSel)
    const thumb = bar?.querySelector('span')
    if (!viewport || !bar || !thumb) return

    const thumbRatio = 0.38
    const canScrub = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    let dragging = false
    let ticking = false
    const row = viewport.firstElementChild

    const cardList = () => (row ? [...row.children] : [])

    const maxScrollLeft = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth)

    /* Fullscreen: all cards fit — snap must not yank to the middle card */
    const contentFits = () => viewport.scrollWidth <= viewport.clientWidth + 4

    const nearestCardIndex = () => {
      const list = cardList()
      if (list.length <= 1) return 0
      const vr = viewport.getBoundingClientRect()
      const center = vr.left + vr.width / 2
      let best = 0
      let bestDist = Infinity
      list.forEach((card, index) => {
        const r = card.getBoundingClientRect()
        const dist = Math.abs(r.left + r.width / 2 - center)
        if (dist < bestDist) {
          bestDist = dist
          best = index
        }
      })
      return best
    }

    /** Card-center progress for snap galleries; scroll edges win on wide screens */
    const galleryProgress = () => {
      const list = cardList()
      const max = maxScrollLeft()
      if (list.length <= 1 || max <= 1) return 0
      if (viewport.scrollLeft <= 2) return 0
      if (viewport.scrollLeft >= max - 2) return 1
      const idx = nearestCardIndex()
      if (idx >= list.length - 1) return 1
      return idx / (list.length - 1)
    }

    const updateBar = () => {
      const fits = contentFits()
      const max = maxScrollLeft()
      viewport.classList.toggle('is-fit', fits)
      bar.classList.toggle('is-idle', fits || max <= 1)

      if (fits && viewport.scrollLeft > 0) {
        viewport.scrollLeft = 0
      }

      const progress = fits ? 0 : galleryProgress()
      thumb.style.left = `${progress * (1 - thumbRatio) * 100}%`
      thumb.style.transform = 'none'
      bar.setAttribute('aria-valuenow', String(Math.round(progress * 100)))
    }

    const requestUpdate = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        updateBar()
      })
    }

    viewport.addEventListener('scroll', requestUpdate, { passive: true })
    viewport.addEventListener('scrollend', updateBar, { passive: true })
    window.addEventListener('resize', requestUpdate, { passive: true })
    viewport.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', requestUpdate, { once: true })
    })
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(requestUpdate).observe(viewport)
      if (row) new ResizeObserver(requestUpdate).observe(row)
    }

    requestAnimationFrame(updateBar)
    window.setTimeout(updateBar, 350)

    /* Touch: visual indicator only — swipe the cards */
    if (!canScrub) {
      bar.classList.add('is-indicator')
      bar.removeAttribute('tabindex')
      return
    }

    const scrubTo = (clientX) => {
      if (contentFits()) return
      const max = maxScrollLeft()
      if (max <= 0) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      const list = cardList()
      if (list.length > 1) {
        const index = Math.round(ratio * (list.length - 1))
        list[index].scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' })
      } else {
        viewport.scrollLeft = ratio * max
      }
      updateBar()
    }

    bar.addEventListener('pointerdown', (event) => {
      if (bar.classList.contains('is-idle') || event.pointerType === 'touch') return
      dragging = true
      try {
        if (event.pointerId != null) bar.setPointerCapture(event.pointerId)
      } catch {
        /* ignore */
      }
      scrubTo(event.clientX)
      event.preventDefault()
    })
    bar.addEventListener('pointermove', (event) => {
      if (!dragging) return
      scrubTo(event.clientX)
    })
    const endDrag = () => {
      dragging = false
    }
    bar.addEventListener('pointerup', endDrag)
    bar.addEventListener('pointercancel', endDrag)

    const stepScroll = (dir) => {
      if (contentFits()) return
      const list = cardList()
      if (!list.length) return
      const current = nearestCardIndex()
      const next = Math.min(list.length - 1, Math.max(0, current + dir))
      list[next].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }

    const onKey = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      stepScroll(event.key === 'ArrowRight' ? 1 : -1)
    }
    viewport.addEventListener('keydown', onKey)
    bar.addEventListener('keydown', onKey)
  }

  bindHScroll('.tb-moments__viewport', '.tb-moments__bar')

  /* Moments photo lightbox */
  const momentsRoot = document.querySelector('[data-moments]')
  if (momentsRoot) {
    const momentModal = momentsRoot.querySelector('[data-moment-modal]')
    const momentCards = [...momentsRoot.querySelectorAll('[data-moment-card]')]
    let momentIndex = 0
    let momentPointerX = 0
    let momentPointerY = 0
    let momentMoved = false
    const MOMENT_TAP_PX = 10

    const fillMomentModal = (index) => {
      if (!momentModal || !momentCards.length) return
      const n = momentCards.length
      momentIndex = ((index % n) + n) % n
      const card = momentCards[momentIndex]
      if (!card) return
      const img = momentModal.querySelector('[data-moment-modal-img]')
      const label = momentModal.querySelector('[data-moment-modal-label]')
      const src = card.getAttribute('data-src') || card.querySelector('img')?.src || ''
      const name = card.getAttribute('data-label') || ''
      if (img) {
        img.src = src
        img.alt = name
      }
      if (label) label.textContent = name
    }

    const openMomentModal = (index) => {
      if (!momentModal) return
      fillMomentModal(index)
      if (typeof momentModal.showModal === 'function') momentModal.showModal()
      else momentModal.setAttribute('open', '')
      document.documentElement.classList.add('tb-modal-open')
      momentModal.querySelector('[data-moment-close]')?.focus()
    }

    const closeMomentModal = () => {
      if (!momentModal) return
      if (typeof momentModal.close === 'function') momentModal.close()
      else momentModal.removeAttribute('open')
      document.documentElement.classList.remove('tb-modal-open')
    }

    momentCards.forEach((card) => {
      card.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        momentPointerX = event.clientX
        momentPointerY = event.clientY
        momentMoved = false
      })
      card.addEventListener('pointermove', (event) => {
        if (
          Math.abs(event.clientX - momentPointerX) > MOMENT_TAP_PX ||
          Math.abs(event.clientY - momentPointerY) > MOMENT_TAP_PX
        ) {
          momentMoved = true
        }
      })
      card.addEventListener('click', (event) => {
        event.preventDefault()
        if (momentMoved) return
        const index = Number(card.dataset.index)
        if (!Number.isNaN(index)) openMomentModal(index)
      })
    })

    if (momentModal) {
      momentModal.querySelector('[data-moment-close]')?.addEventListener('click', closeMomentModal)
      momentModal.querySelector('[data-moment-prev]')?.addEventListener('click', () => {
        fillMomentModal(momentIndex - 1)
      })
      momentModal.querySelector('[data-moment-next]')?.addEventListener('click', () => {
        fillMomentModal(momentIndex + 1)
      })
      momentModal.addEventListener('click', (event) => {
        if (event.target === momentModal) closeMomentModal()
      })
      momentModal.addEventListener('close', () => {
        document.documentElement.classList.remove('tb-modal-open')
      })
      document.addEventListener('keydown', (event) => {
        if (!momentModal.open) return
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          fillMomentModal(momentIndex - 1)
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          fillMomentModal(momentIndex + 1)
        }
      })
    }
  }

  /* Scroll reveals — sections + staggered cards fade/rise into place */
  const prefersReducedReveal = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const revealSections = [...document.querySelectorAll('.tb-page > .shopify-section')].filter(
    (section) => !section.querySelector('.tb-header, .tb-hero'),
  )

  const staggerSelector =
    '.tb-section-head, .tb-moments__viewport, .tb-moments__bar, .tb-reviews__fan, .tb-reviews__hint, .tb-adventures__photo, .tb-adventures__copy, .tb-how__step, .tb-how__samples-head, .tb-how__sample, .tb-cta__banner, .tb-cta-finale__stage, .tb-cta-finale__content, .tb-footer__grid > *'

  const armSection = (section) => {
    section.classList.add('tb-reveal')
    ;[...section.querySelectorAll(staggerSelector)].forEach((el, index) => {
      el.classList.add('tb-reveal-item')
      el.style.setProperty('--tb-reveal-delay', `${Math.min(index, 7) * 75}ms`)
    })
  }

  const showSection = (section) => {
    section.classList.add('is-in')
  }

  if (prefersReducedReveal || !('IntersectionObserver' in window)) {
    revealSections.forEach((section) => {
      armSection(section)
      showSection(section)
    })
  } else {
    revealSections.forEach(armSection)

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          showSection(entry.target)
          io.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 },
    )

    revealSections.forEach((section) => {
      const rect = section.getBoundingClientRect()
      const vh = window.innerHeight || 800
      if (rect.top < vh * 0.88 && rect.bottom > 48) {
        showSection(section)
        return
      }
      io.observe(section)
    })

    window.setTimeout(() => revealSections.forEach(showSection), 2400)
  }

  if (!page) return

  const prefersReduced = prefersReducedReveal
  const supportsVT =
    'CSSViewTransitionRule' in window ||
    CSS.supports?.('view-transition-name', 'none') ||
    'startViewTransition' in document

  if (prefersReduced) {
    root.classList.add('tb-ready')
    return
  }

  if (!supportsVT) {
    requestAnimationFrame(() => root.classList.add('tb-ready'))
  } else {
    root.classList.add('tb-ready')
  }

  const sameOrigin = (url) => {
    try {
      const next = new URL(url, window.location.href)
      return next.origin === window.location.origin
    } catch {
      return false
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      if (prefersReduced || supportsVT) return
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const link = event.target.closest('a[href]')
      if (!link) return
      if (link.target && link.target !== '_self') return
      if (link.hasAttribute('download')) return

      const href = link.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return
      if (!sameOrigin(href)) return

      const next = new URL(href, window.location.href)
      if (next.pathname === window.location.pathname && next.search === window.location.search) return

      event.preventDefault()
      root.classList.add('tb-leaving')
      window.setTimeout(() => {
        window.location.href = next.href
      }, 150)
    },
    true,
  )

  /* Email signup popup */
  const emailPopup = document.querySelector('[data-email-popup]')
  if (emailPopup) {
    const storageKey = emailPopup.getAttribute('data-storage-key') || 'w2w-email-popup-v1'
    const delay = Number(emailPopup.getAttribute('data-delay') || 5000)
    const hasSuccess = Boolean(emailPopup.querySelector('[data-email-popup-success]'))
    const skipPaths = ['/pages/create-guide', '/checkouts', '/cart']
    const path = window.location.pathname || ''
    const shouldSkip = skipPaths.some((p) => path.includes(p))

    const markDone = () => {
      try {
        window.localStorage.setItem(storageKey, '1')
      } catch (_) {
        /* ignore */
      }
    }

    const alreadyDone = () => {
      try {
        return window.localStorage.getItem(storageKey) === '1'
      } catch (_) {
        return false
      }
    }

    const openPopup = () => {
      if (typeof emailPopup.showModal === 'function') emailPopup.showModal()
      else emailPopup.setAttribute('open', '')
      document.documentElement.classList.add('tb-modal-open')
      emailPopup.querySelector('[data-email-popup-input]')?.focus()
    }

    const closePopup = () => {
      if (typeof emailPopup.close === 'function') emailPopup.close()
      else emailPopup.removeAttribute('open')
      document.documentElement.classList.remove('tb-modal-open')
      markDone()
    }

    emailPopup.querySelectorAll('[data-email-popup-close]').forEach((btn) => {
      btn.addEventListener('click', closePopup)
    })
    emailPopup.addEventListener('click', (event) => {
      if (event.target === emailPopup) closePopup()
    })
    emailPopup.addEventListener('close', () => {
      document.documentElement.classList.remove('tb-modal-open')
      markDone()
    })

    if (hasSuccess) {
      markDone()
      openPopup()
      window.setTimeout(closePopup, 2800)
    } else if (!shouldSkip && !alreadyDone()) {
      window.setTimeout(() => {
        if (alreadyDone()) return
        openPopup()
      }, Number.isFinite(delay) ? Math.max(800, delay) : 5000)
    }
  }
})()
